import { useMemo } from 'react';

import { resolveExposedColumns } from './useWidgetBuilderForm.js';

/**
 * Maps builder state into the object the shared preview renderer draws
 * (CT-5199).
 *
 * **`name` is deliberately absent.** The card title is passed to
 * `LivePreviewCard` separately, so typing a name re-renders the title and
 * nothing else — putting `name` in these deps would redraw the whole table or
 * chart on every keystroke, which is exactly the regression the ticket calls out.
 *
 * No service call here either: this hook maps *configuration*, and the result
 * it is drawn with is fetched separately by `LivePreviewPane` through
 * `widget/execute`. Keeping the two apart is what lets the pane fall back to
 * the design-system sample whenever there is nothing saved to execute.
 */
export const useLivePreviewWidget = ({ form, columns }) => {
  const { dataset, type, validFilters, endUserFiltersEnabled } = form;

  const exposedColumns = useMemo(
    () => resolveExposedColumns(dataset.exposedColumns, columns),
    [columns, dataset.exposedColumns],
  );

  // What the record set exposes is what the preview shows — there is no
  // separate "visible columns" setting since the Appearance step was dropped.
  const visibleColumns = exposedColumns;

  /** Filter chips, from the **complete** rows only (CT-5193). */
  const filterChips = useMemo(
    () =>
      validFilters.map((filter) => ({
        id: filter.id,
        // `filter.column` is a `fieldUuid`, not the field's code — see
        // `useColumnPicker`/`buildWidgetPayload`.
        label: columns.find((c) => c.fieldUuid === filter.column)?.displayName ?? filter.column,
        operator: filter.operator,
        value: filter.value,
      })),
    [columns, validFilters],
  );

  const previewWidget = useMemo(
    () => ({
      type,
      source: dataset.sourceId,
      entityRef: dataset.entityRef,
      datasetId: dataset.datasetId,
      visibleColumns,
      endUserFiltersEnabled,
    }),
    [
      dataset.datasetId,
      dataset.entityRef,
      dataset.sourceId,
      endUserFiltersEnabled,
      type,
      visibleColumns,
    ],
  );

  /**
   * Which of the two progressive empty states applies, or `null` once there is
   * enough configuration to draw something.
   */
  const emptyStateKey = useMemo(() => {
    if (!dataset.sourceId) return 'widgetBuilder.preview.noSource';
    if (!type) return 'widgetBuilder.preview.noType';
    return null;
  }, [dataset.sourceId, type]);

  return useMemo(
    () => ({ previewWidget, filterChips, emptyStateKey, visibleColumns }),
    [emptyStateKey, filterChips, previewWidget, visibleColumns],
  );
};

export default useLivePreviewWidget;
